export function readDestinations(args: unknown): string[] {
  if (!args || typeof args !== 'object') return [];
  const a = args as Record<string, unknown>;
  if (Array.isArray(a.destinations)) {
    return a.destinations.filter((d): d is string => typeof d === 'string' && d.length > 0);
  }
  if (Array.isArray(a.destination)) {
    return a.destination.filter((d): d is string => typeof d === 'string' && d.length > 0);
  }
  const single = a.destination ?? a.cityName ?? a.city;
  if (typeof single === 'string' && single.length > 0) return [single];
  return [];
}

interface ResolvedDestinationsLabel {
  destinations: string;
  hasUnresolved: boolean;
}

/**
 * Maps IATA codes to localized names. Returns `hasUnresolved=true` while any
 * code is still pending resolution so callers can hide the label entirely
 * rather than render raw codes like "ATH".
 */
export function resolveDestinationsLabel({
  codes,
  getDestinationName,
}: {
  codes: string[];
  getDestinationName?: (code: string) => string;
}): ResolvedDestinationsLabel | undefined {
  if (codes.length === 0) return undefined;
  if (!getDestinationName) return { destinations: '', hasUnresolved: true };
  const names: string[] = [];
  let hasUnresolved = false;
  for (const code of codes) {
    const name = getDestinationName(code);
    if (name === code) hasUnresolved = true;
    names.push(name);
  }
  return { destinations: names.join(', '), hasUnresolved };
}

export function readOrigins(args: unknown): string[] {
  if (!args || typeof args !== 'object') return [];
  const a = args as Record<string, unknown>;
  const single = a.origin;
  if (typeof single === 'string' && single.length > 0) return [single];
  return [];
}

/** Count of non-empty destination entries in `args.destinations` / `args.destination`. */
export function readDestinationsCount(args: unknown): number {
  return readDestinations(args).length;
}

/** Reads a single non-empty string field from args by key. Returns undefined when missing, wrong type, or empty. */
export function readStringArg({ args, key }: { args: unknown; key: string }): string | undefined {
  if (!args || typeof args !== 'object') return undefined;
  const value = (args as Record<string, unknown>)[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

export interface IsoDateRange {
  start: string;
  end: string;
}

/** Top-level `earliestDepartureDate` / `latestDepartureDate`, the date-discovery shape flight suppliers share. */
export function readDepartureDateRange(args: unknown): IsoDateRange | undefined {
  const start = readStringArg({ args, key: 'earliestDepartureDate' });
  const end = readStringArg({ args, key: 'latestDepartureDate' });
  return start && end ? { start, end } : undefined;
}

/** Top-level `fromDate` / `toDate`, the date-discovery shape hotel suppliers share. */
export function readFromToDateRange(args: unknown): IsoDateRange | undefined {
  const start = readStringArg({ args, key: 'fromDate' });
  const end = readStringArg({ args, key: 'toDate' });
  return start && end ? { start, end } : undefined;
}

/** Reads a single string `args.destination` (Fattal getRedirectHotels shape — destination is scalar, not array). */
export function readSingleDestination(args: unknown): string | undefined {
  return readStringArg({ args, key: 'destination' });
}

/** Length of `args[key]` when it's a non-empty array, otherwise 0. */
export function readArrayArgCount({ args, key }: { args: unknown; key: string }): number {
  if (!args || typeof args !== 'object') return 0;
  const value = (args as Record<string, unknown>)[key];
  return Array.isArray(value) ? value.length : 0;
}

/** Filters `args[key]` to a string array. Returns empty when missing or wrong shape. */
export function readStringArrayArg({
  args,
  key,
}: {
  args: unknown;
  key: string;
}): readonly string[] {
  if (!args || typeof args !== 'object') return [];
  const value = (args as Record<string, unknown>)[key];
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string');
}

/**
 * Standard `getResultStatus` for tools whose result body is an object with a
 * single array under `key` (e.g. `{ hotels: [...] }`). Returns `'results'`
 * when the array has entries and `'empty'` otherwise.
 */
export function makeArrayKeyResultExtractors(key: string): {
  getResultStatus: (args: unknown, result: unknown) => 'results' | 'empty';
} {
  return {
    getResultStatus: (_args, result) =>
      readArrayArgCount({ args: result, key }) > 0 ? 'results' : 'empty',
  };
}
